<script setup lang="ts">
import { Models } from '@hantera/portal-app';
import { PhoneInput, TextInput } from '@hantera/design-system';
import { computed, ref, useTemplateRef, watch } from 'vue';
import CountrySelect from '@/components/CountrySelect.vue';
import postalCodeValidator from '@/postal-code-validator';

const props = defineProps<{
  modelValue: Models.Graph.Address
  allowedCountries?: string[]
}>();

defineExpose({
  validate,
});

const emit = defineEmits<{
  (event: 'update:modelValue', value: Models.Graph.Address): void
}>();

// TODO State select
// TODO Localization

const current = ref({ ...props.modelValue });

const elemName = ref<typeof TextInput>();
const elemAddress1 = ref<typeof TextInput>();
const elemPostalCode = ref<typeof TextInput>();
const elemCountryCode = useTemplateRef<InstanceType<typeof CountrySelect>>('elemCountryCode');
const elemCity = ref<typeof TextInput>();
const elemEmail = ref<typeof TextInput>();
const elemPhone = ref<typeof PhoneInput>();
const phoneIsValid = ref<boolean>(false);

watch(props.modelValue, (val) => current.value = { ...val });
watch(current.value, () => {
  emit('update:modelValue', current.value);
});

const states: { [code: string]: string[] } = {
  "US": ["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"],
  "CA": ["AB", "BC", "MB", "NB", "NL", "NT", "NS", "NU", "ON", "PE", "QC", "SK", "YT"],
  "AU": ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"],
  "GB": ["ENG", "NIR", "SCT", "WLS"],
  "DE": ["BW", "BY", "BE", "BB", "HB", "HH", "HE", "MV", "NI", "NW", "RP", "SL", "SN", "ST", "SH", "TH"],
}

const disableState = computed(() => {
  if (current.value.state && current.value.state.length > 0) {
    // Always allow state to be set if it already has a value
    return false
  }

  return !current.value.countryCode || typeof states[current.value.countryCode.toUpperCase()] === 'undefined';
});

function validate(): boolean {
  let valid = true;
  if (current.value.name.trim().length < 2) {
    elemName.value?.setError('Required');
    valid = false;
  }
  if (current.value.addressLine1.trim().length < 2) {
    elemAddress1.value?.setError('Required');
    valid = false;
  }
  if (current.value.postalCode.trim().length === 0) {
    elemPostalCode.value?.setError('Required');
    valid = false;
  }

  if (current.value.countryCode.length === 0) {
    elemCountryCode.value?.setError('Invalid');
    valid = false;
  }
  else if (!postalCodeValidator(current.value.countryCode, current.value.postalCode)) {
    elemPostalCode.value?.setError('Invalid');
    valid = false;
  }

  if (current.value.city.trim().length < 2) {
    elemCity.value?.setError('Required');
    valid = false;
  }
  if (current.value.email.trim().length <= 5) {
    elemEmail.value?.setError('Required');
    valid = false;
  }
  if (!phoneIsValid.value) {
    elemPhone.value?.setError('Invalid');
    valid = false;
  }

  return valid;
}

</script>

<template>
  <fieldset>
    <div class="address-editor__name">
      <div>
        <label>Name</label>
        <TextInput
          v-model="current.name"
          ref="elemName"
          autocomplete="off"
        />
      </div>
    </div>
    <div>
      <label>Address</label>
      <TextInput
        v-model="current.addressLine1"
        ref="elemAddress1"
      />
    </div>
    <div>
      <TextInput v-model="current.addressLine2" />
    </div>
    <div class="address-editor__city">
      <div class="address-editor__postal-code">
        <label>Postal code</label>
        <TextInput
          v-model="current.postalCode"
          ref="elemPostalCode"
        />
      </div>
      <div>
        <label>City</label>
        <TextInput
          v-model="current.city"
          ref="elemCity"
        />
      </div>
    </div>
    <div class="address-editor__country-and-state">
      <div>
        <label>Country</label>
        <CountrySelect
          v-model="current.countryCode"
          ref="elemCountryCode"
          :allowed-countries="allowedCountries"
        />
      </div>
      <div>
        <label>State</label>
        <TextInput
          v-model="current.state"
          :disabled="disableState"
        />
      </div>
    </div>
    <div>
      <label>E-mail</label>
      <TextInput
        v-model="current.email"
        ref="elemEmail"
      />
    </div>
    <div>
      <label>Phone</label>
      <PhoneInput
        v-model="current.phone"
        ref="elemPhone"
        v-model:is-valid="phoneIsValid"
        :default-country="current.countryCode"
      />
    </div>
    <slot></slot>
  </fieldset>
</template>

<style>
.address-editor__name,
.address-editor__city,
.address-editor__country-and-state {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  gap: 5px;
}

.address-editor__city {
  grid-template-columns: 100px auto;
}

.address-editor__country-and-state {
  grid-template-columns: 1fr 1fr;
}
</style>